import React, { useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { CheckCircle } from 'lucide-react';

interface EventType {
  id: string;
  name: string;
  color: string;
}

interface Employee {
  id: string;
  firstName: string;
  lastName: string;
}

interface EventConfirmationProps {
  employee: Employee;
  eventType: EventType;
  timestamp: Date;
  onClose: () => void;
  autoCloseDelay?: number;
}

export const EventConfirmation: React.FC<EventConfirmationProps> = ({
  employee,
  eventType,
  timestamp,
  onClose,
  autoCloseDelay = 3000
}) => {
  useEffect(() => {
    const timer = setTimeout(() => {
      onClose();
    }, autoCloseDelay);

    return () => clearTimeout(timer);
  }, [onClose, autoCloseDelay]);

  const formatTime = (date: Date) => {
    return new Intl.DateTimeFormat('pl-PL', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    }).format(date);
  };

  const formatDate = (date: Date) => {
    return new Intl.DateTimeFormat('pl-PL', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    }).format(date);
  };

  return (
    <div className="w-full max-w-lg mx-auto">
      <Card className="border-green-200 bg-green-50">
        <CardHeader className="text-center">
          <div className="flex justify-center mb-4">
            <CheckCircle className="w-16 h-16 text-green-600" />
          </div>
          <CardTitle className="text-2xl font-bold text-green-800">
            Zdarzenie zarejestrowane!
          </CardTitle>
          <CardDescription className="text-lg text-green-700">
            Dziękujemy, {employee.firstName}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4 text-center">
          {/* Event Details */}
          <div className="space-y-2">
            <div className="flex items-center justify-center space-x-2">
              <div
                className="w-4 h-4 rounded-full"
                style={{ backgroundColor: eventType.color }}
              />
              <span className="font-semibold text-lg">{eventType.name}</span>
            </div>
            
            <div className="text-2xl font-bold text-gray-800">
              {formatTime(timestamp)}
            </div>
            
            <div className="text-sm text-gray-600">
              {formatDate(timestamp)}
            </div>
          </div>

          {/* Employee Info */}
          <div className="bg-white p-4 rounded-lg">
            <p className="text-sm text-gray-600">Pracownik:</p>
            <p className="font-semibold">{employee.firstName} {employee.lastName}</p>
          </div>

          {/* Auto-close info */}
          <div className="text-sm text-gray-500">
            Automatyczne wylogowanie za {Math.ceil(autoCloseDelay / 1000)} sekund
          </div>

          {/* Manual close button */}
          <Button
            variant="outline"
            onClick={onClose}
            className="mt-4"
          >
            Zamknij teraz
          </Button>
        </CardContent>
      </Card>
    </div>
  );
};